import type { ApiSchemas } from '@rental/contracts/client'

type Place = ApiSchemas['CargosPlace']

/** The renter's details as the counter types them (decision 5). */
export interface IdentityDraft {
  surname: string
  givenNames: string
  birthDate: string
  birthPlace: Place | null
  citizenship: Place | null
  address: string
  postalCode: string
  noPostalCode: boolean
  city: string
  country: string
  residencePlace: Place | null
  addressSource: 'document' | 'declared' | ''
  documentType: string
  documentNumber: string
  documentPlace: Place | null
  licenceNumber: string
  licencePlace: Place | null
  phone: string
}

/** A phone in international form, as the server takes it. */
export const E164 = /^\+[1-9][0-9]{6,14}$/

/** A phone as typed, without the spaces, dots, dashes and brackets people write. */
export const compactPhone = (phone: string) => phone.replace(/[\s().-]/g, '')

/** Something the identity form still lacks before it can be recorded. */
export type IdentityReason =
  | 'surname'
  | 'givenNames'
  | 'birthDate'
  | 'birthPlace'
  | 'citizenship'
  | 'address'
  | 'postalCode'
  | 'city'
  | 'country'
  | 'residencePlace'
  | 'addressSource'
  | 'documentType'
  | 'documentNumber'
  | 'documentPlace'
  | 'licenceNumber'
  | 'licencePlace'
  | 'phone'
  | 'phoneRequired'

/**
 * What the identity form still lacks, each said beside its button (DESIGN.md:
 * never a disabled button with no reason), in the page's order. CaRGOS's
 * details are asked for only where CaRGOS applies; a phone is optional, but
 * one typed must be international. The server checks each again.
 *
 * Q-162: a second driver's form asks no home address (CaRGOS's CONDUCENTE2
 * has none), and where CaRGOS applies their phone is required: it is in the
 * record (CONDUCENTE2_RECAPITO).
 */
export function identityReasons(
  draft: IdentityDraft,
  cargos: boolean,
  driver: 'main' | 'second' = 'main',
): IdentityReason[] {
  const phone = compactPhone(draft.phone)
  const renter = driver === 'main'
  const lacking: readonly (readonly [IdentityReason, boolean])[] = [
    ['surname', draft.surname.trim() === ''],
    ['givenNames', draft.givenNames.trim() === ''],
    ['birthDate', draft.birthDate === ''],
    ['birthPlace', cargos && draft.birthPlace === null],
    ['citizenship', cargos && draft.citizenship === null],
    ['address', renter && draft.address.trim().length < 3],
    ['postalCode', renter && !draft.noPostalCode && draft.postalCode.trim() === ''],
    ['city', renter && draft.city.trim() === ''],
    ['country', renter && draft.country === ''],
    ['residencePlace', renter && cargos && draft.residencePlace === null],
    ['addressSource', renter && draft.addressSource === ''],
    ['documentType', cargos && draft.documentType === ''],
    ['documentNumber', cargos && draft.documentNumber.trim() === ''],
    ['documentPlace', cargos && draft.documentPlace === null],
    ['licenceNumber', cargos && draft.licenceNumber.trim() === ''],
    ['licencePlace', cargos && draft.licencePlace === null],
    ['phoneRequired', !renter && cargos && phone === ''],
    ['phone', phone !== '' && !E164.test(phone)],
  ]
  return lacking.filter(([, missing]) => missing).map(([reason]) => reason)
}
